import { useState, useEffect } from 'react';
import { Download, RotateCw, RotateCcw } from 'lucide-react';
import { PDFDocument, degrees } from 'pdf-lib';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';

export function PdfRotate() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [pages, setPages] = useState('all');
  const [pageCount, setPageCount] = useState(0);
  const [angle, setAngle] = useState(90);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    const buf = await readFileAsArrayBuffer(f);
    const doc = await PDFDocument.load(buf);
    setPageCount(doc.getPageCount());
  }

  function targetIndices(total: number): number[] {
    if (pages === 'all') return Array.from({ length: total }, (_, i) => i);
    const set = new Set<number>();
    for (const part of pages.split(',')) {
      const m = part.trim().match(/^(\d+)(?:-(\d+))?$/);
      if (!m) continue;
      const a = parseInt(m[1], 10) - 1;
      const b = m[2] ? parseInt(m[2], 10) - 1 : a;
      for (let i = a; i <= b; i++) if (i >= 0 && i < total) set.add(i);
    }
    return [...set];
  }

  async function run() {
    if (!file) return;
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(file);
      const doc = await PDFDocument.load(buf);
      const idx = targetIndices(doc.getPageCount());
      idx.forEach((i) => {
        const page = doc.getPage(i);
        const cur = page.getRotation().angle ?? 0;
        page.setRotation(degrees((cur + angle) % 360));
      });
      const bytes = await doc.save();
      saveAs(
        bytesToBlob(bytes),
        file.name.replace(/\.pdf$/i, '') + '-rotated.pdf',
      );
      toast.success('Saved');
    } catch (e) {
      toast.error('Rotation failed');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="pdf-rotate"
        title="Rotate PDF pages"
        description="Rotate any subset of pages by 90/180/270 degrees."
        badges={pageCount > 0 ? <Badge>{pageCount} pages</Badge> : null}
      />

      {!file ? (
        <Dropzone accept="application/pdf" onFiles={handleFile} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium truncate">{file.name}</span>
              <Button size="sm" variant="ghost" onClick={() => setFile(null)}>
                Change
              </Button>
            </div>
            <Field label="Pages" hint='"all" or "1-3, 5"'>
              <Input value={pages} onChange={(e) => setPages(e.target.value)} className="font-mono" />
            </Field>
            <div>
              <div className="text-xs font-medium text-fg-muted mb-2">Rotation</div>
              <div className="grid grid-cols-3 gap-2">
                {[90, 180, 270].map((a) => (
                  <button
                    key={a}
                    onClick={() => setAngle(a)}
                    className={`flex items-center justify-center gap-2 py-3 rounded-lg border transition ${
                      angle === a
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-border hover:border-border-strong'
                    }`}
                  >
                    {a === 270 ? <RotateCcw size={14} /> : <RotateCw size={14} />}
                    <span className="text-sm font-mono">{a}°</span>
                  </button>
                ))}
              </div>
            </div>
          </Card>
          <Card>
            <Button
              variant="primary"
              className="w-full"
              disabled={working || !pageCount}
              loading={working}
              onClick={run}
            >
              <Download size={12} />
              Rotate &amp; save
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}

export default PdfRotate;
